import {
  ArrowDownRight,
  Bot,
  Boxes,
  BrainCircuit,
  CheckCircle2,
  Database,
  GitBranch,
  LineChart,
  LucideIcon,
  Network,
  Orbit,
  ShieldCheck,
  Sparkles,
  Zap,
} from 'lucide-react'

export type VisualVariant =
  | 'about'
  | 'ai'
  | 'cases'
  | 'cto'
  | 'innovation'
  | 'resources'
  | 'workshops'

type VisualConfig = {
  accent: string
  after: string[]
  before: string[]
  graph: Array<{ label: string; value: number }>
  heroLabel: string
  Icon: LucideIcon
  motif: 'agent' | 'arc' | 'browser' | 'flow' | 'lab' | 'library' | 'radar'
  nodes: Array<{ label: string; Icon: LucideIcon }>
  outcome: string
  tint: string
}

const visualConfigs: Record<VisualVariant, VisualConfig> = {
  about: {
    accent: 'from-sky-300 to-cyan-100',
    Icon: Orbit,
    heroLabel: 'Operator Map',
    motif: 'radar',
    outcome: 'Calm execution',
    tint: 'bg-sky-300/10',
    nodes: [
      { label: 'Business', Icon: LineChart },
      { label: 'Product', Icon: Boxes },
      { label: 'Engineering', Icon: Network },
      { label: 'AI', Icon: BrainCircuit },
    ],
    before: ['Ideas scattered across tools', 'Technical risk explained too late', 'Founder intuition trapped in meetings'],
    after: ['Shared operating map', 'Tradeoffs visible early', 'Decisions tied to delivery'],
    graph: [
      { label: 'Clarity', value: 88 },
      { label: 'Ownership', value: 74 },
      { label: 'Velocity', value: 81 },
    ],
  },
  ai: {
    accent: 'from-emerald-300 to-sky-300',
    Icon: BrainCircuit,
    heroLabel: 'AI Readiness Circuit',
    motif: 'agent',
    outcome: 'Production AI',
    tint: 'bg-emerald-300/10',
    nodes: [
      { label: 'Data', Icon: Database },
      { label: 'Context', Icon: Network },
      { label: 'Model', Icon: Bot },
      { label: 'Evaluation', Icon: CheckCircle2 },
    ],
    before: ['Tool-first pilots', 'No evaluation loop', 'Unclear owner for risk'],
    after: ['Workflow-first roadmap', 'Measurable quality gates', 'Governed rollout path'],
    graph: [
      { label: 'Feasibility', value: 78 },
      { label: 'Governance', value: 84 },
      { label: 'ROI Signal', value: 72 },
    ],
  },
  cases: {
    accent: 'from-amber-200 to-sky-300',
    Icon: LineChart,
    heroLabel: 'Leak Diagnosis',
    motif: 'arc',
    outcome: 'Priority sequence',
    tint: 'bg-amber-300/10',
    nodes: [
      { label: 'Traffic', Icon: Zap },
      { label: 'Platform', Icon: Boxes },
      { label: 'Conversion', Icon: LineChart },
      { label: 'Ops', Icon: GitBranch },
    ],
    before: ['Symptoms treated one by one', 'Metrics disconnected from tech', 'Expensive fixes chosen too soon'],
    after: ['Revenue leak mapped', 'Constraint tied to metric', 'Fixes sequenced by leverage'],
    graph: [
      { label: 'Leak Found', value: 86 },
      { label: 'Waste Cut', value: 62 },
      { label: 'Signal Lift', value: 91 },
    ],
  },
  cto: {
    accent: 'from-sky-300 to-indigo-200',
    Icon: Network,
    heroLabel: 'CTO Circuit',
    motif: 'flow',
    outcome: 'Technical truth',
    tint: 'bg-sky-300/10',
    nodes: [
      { label: 'Founder', Icon: Sparkles },
      { label: 'Roadmap', Icon: GitBranch },
      { label: 'Stack', Icon: Database },
      { label: 'Team', Icon: Boxes },
    ],
    before: ['Roadmap churn', 'Vendor ambiguity', 'Architecture drag'],
    after: ['Decision cadence', 'Clear ownership', 'Build/buy/pause clarity'],
    graph: [
      { label: 'Focus', value: 83 },
      { label: 'Risk Control', value: 77 },
      { label: 'Delivery', value: 80 },
    ],
  },
  innovation: {
    accent: 'from-fuchsia-200 to-sky-300',
    Icon: Bot,
    heroLabel: 'Product Wedge',
    motif: 'browser',
    outcome: 'Pilot to product',
    tint: 'bg-fuchsia-300/10',
    nodes: [
      { label: 'Tabs', Icon: Boxes },
      { label: 'Agents', Icon: Bot },
      { label: 'Scoring', Icon: LineChart },
      { label: 'Memo', Icon: CheckCircle2 },
    ],
    before: ['Manual tab review', 'Rules stuck in spreadsheets', 'Deal memory lost'],
    after: ['Agent-assisted review', 'Portfolio fit scoring', 'Reusable decision system'],
    graph: [
      { label: 'Speed', value: 89 },
      { label: 'Fit Score', value: 76 },
      { label: 'Repeatability', value: 86 },
    ],
  },
  resources: {
    accent: 'from-teal-200 to-sky-300',
    Icon: ShieldCheck,
    heroLabel: 'Learning Path',
    motif: 'library',
    outcome: 'Better call prep',
    tint: 'bg-teal-300/10',
    nodes: [
      { label: 'Checklist', Icon: CheckCircle2 },
      { label: 'Models', Icon: BrainCircuit },
      { label: 'Templates', Icon: Boxes },
      { label: 'Decision', Icon: ArrowDownRight },
    ],
    before: ['Problem hard to frame', 'Terms feel fuzzy', 'Questions stay generic'],
    after: ['Constraint named', 'Shared vocabulary', 'Sharper diagnostic'],
    graph: [
      { label: 'Readiness', value: 72 },
      { label: 'Language', value: 84 },
      { label: 'Confidence', value: 78 },
    ],
  },
  workshops: {
    accent: 'from-sky-300 to-emerald-300',
    Icon: Sparkles,
    heroLabel: 'Workshop Lab',
    motif: 'lab',
    outcome: 'Working artifacts',
    tint: 'bg-emerald-300/10',
    nodes: [
      { label: 'Prompt', Icon: Sparkles },
      { label: 'Schema', Icon: Database },
      { label: 'Prototype', Icon: Boxes },
      { label: 'Demo', Icon: Zap },
    ],
    before: ['AI awareness without usage', 'Teams using different language', 'No reusable artifacts'],
    after: ['Shared operating model', 'Hands-on prototypes', 'Templates teams can reuse'],
    graph: [
      { label: 'Fluency', value: 82 },
      { label: 'Adoption', value: 68 },
      { label: 'Artifacts', value: 90 },
    ],
  },
}

export function HeroSystemVisual({ variant }: { variant: VisualVariant }) {
  const config = visualConfigs[variant]
  const Icon = config.Icon

  return (
    <div className="relative overflow-hidden rounded-[1.5rem] border border-slate-800 bg-slate-950 p-5 shadow-[0_24px_80px_rgba(2,6,23,0.42)]">
      <div className="absolute inset-0 visual-grid opacity-70" aria-hidden="true" />
      <div className={`absolute -right-16 -top-16 h-48 w-48 rounded-full ${config.tint} blur-3xl`} aria-hidden="true" />
      <div className="relative">
        <div className="mb-5 overflow-hidden rounded-[1.15rem] border border-slate-800 bg-slate-900">
          <VariantHeaderGraphic config={config} variant={variant} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-sky-300">{config.heroLabel}</p>
            <h2 className="mt-2 text-2xl font-black text-white">Compute factors</h2>
          </div>
          <div className="rounded-xl border border-sky-300/30 bg-sky-300/10 p-3 text-sky-200">
            <Icon className="h-6 w-6" />
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {config.nodes.map(({ label, Icon: NodeIcon }, index) => (
            <div
              key={label}
              className="group rounded-2xl border border-slate-800 bg-slate-900/80 p-4 transition hover:border-sky-300/60 hover:bg-slate-900"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-950 text-sky-300">
                  <NodeIcon className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    Node {index + 1}
                  </p>
                  <p className="font-semibold text-slate-100">{label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-5 rounded-2xl border border-sky-300/25 ${config.tint} p-4`}>
          <p className="text-sm font-semibold text-sky-100">Output</p>
          <p className="mt-1 text-2xl font-black text-white">{config.outcome}</p>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-800">
            <div className={`h-full w-[82%] rounded-full bg-gradient-to-r ${config.accent}`} />
          </div>
        </div>
      </div>
    </div>
  )
}

function VariantHeaderGraphic({ config, variant }: { config: VisualConfig; variant: VisualVariant }) {
  const topNodes = config.nodes.slice(0, 4)

  if (config.motif === 'lab') {
    return (
      <div className="relative h-44 overflow-hidden bg-slate-950 p-5">
        <div className="absolute inset-0 visual-grid opacity-50" />
        <div className="relative grid h-full grid-cols-[0.8fr_1.2fr] gap-4">
          <div className="rounded-2xl border border-sky-300/25 bg-sky-300/10 p-4">
            <Sparkles className="h-7 w-7 text-sky-200" />
            <div className="mt-8 h-2 rounded-full bg-sky-300" />
            <div className="mt-3 h-2 w-2/3 rounded-full bg-emerald-300" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {topNodes.map(({ label, Icon }) => (
              <div key={label} className="rounded-2xl border border-slate-700 bg-slate-900/80 p-3">
                <Icon className="h-5 w-5 text-sky-300" />
                <p className="mt-5 text-sm font-bold text-white">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (config.motif === 'browser') {
    return (
      <div className="relative h-44 overflow-hidden bg-slate-950 p-5">
        <div className="absolute inset-0 visual-grid opacity-40" />
        <div className="relative rounded-2xl border border-fuchsia-200/25 bg-slate-900/80 p-4">
          <div className="flex gap-2">
            <span className="h-2 w-2 rounded-full bg-fuchsia-200" />
            <span className="h-2 w-2 rounded-full bg-sky-300" />
            <span className="h-2 w-2 rounded-full bg-emerald-300" />
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {topNodes.slice(0, 3).map(({ label }) => (
              <div key={label} className="rounded-xl border border-slate-700 bg-slate-950 p-3">
                <div className={`h-2 rounded-full bg-gradient-to-r ${config.accent}`} />
                <p className="mt-8 text-xs font-bold text-slate-200">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (config.motif === 'agent') {
    return (
      <div className="relative h-44 overflow-hidden bg-slate-950 p-5">
        <div className="absolute left-1/2 top-1/2 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-300/35" />
        <div className="absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-300/35 bg-emerald-300/10" />
        <BrainCircuit className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 text-emerald-200" />
        {topNodes.map(({ label, Icon }, index) => (
          <div
            key={label}
            className="absolute rounded-xl border border-slate-700 bg-slate-900/90 p-2 text-sky-200"
            style={[
              { left: '7%', top: '16%' },
              { right: '7%', top: '16%' },
              { left: '7%', bottom: '16%' },
              { right: '7%', bottom: '16%' },
            ][index]}
          >
            <Icon className="h-4 w-4" />
          </div>
        ))}
      </div>
    )
  }

  if (config.motif === 'arc') {
    return (
      <div className="relative h-44 overflow-hidden bg-slate-950 p-5">
        <div className="absolute -bottom-20 left-8 h-56 w-56 rounded-full border-[18px] border-amber-200/15" />
        <div className="absolute bottom-6 left-10 right-10 h-20 rounded-t-full border-x border-t border-sky-300/40" />
        <LineChart className="absolute bottom-12 left-1/2 h-12 w-12 -translate-x-1/2 text-amber-100" />
        <div className="absolute right-6 top-6 space-y-3">
          {[86, 62, 91].map((width) => (
            <div key={width} className="h-2 w-32 rounded-full bg-slate-800">
              <div className={`h-full rounded-full bg-gradient-to-r ${config.accent}`} style={{ width: `${width}%` }} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (config.motif === 'library') {
    return (
      <div className="relative h-44 overflow-hidden bg-slate-950 p-5">
        <div className="grid h-full grid-cols-4 gap-3">
          {topNodes.map(({ label, Icon }, index) => (
            <div key={label} className="flex flex-col justify-between rounded-2xl border border-teal-200/25 bg-teal-300/10 p-3">
              <Icon className="h-5 w-5 text-teal-100" />
              <div>
                <div className="h-2 rounded-full bg-slate-700" />
                <div className={`mt-2 h-2 rounded-full bg-gradient-to-r ${config.accent}`} style={{ width: `${70 + index * 7}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (config.motif === 'radar') {
    return (
      <div className="relative h-44 overflow-hidden bg-slate-950 p-5">
        <div className="absolute left-1/2 top-1/2 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-300/20" />
        <div className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-300/30" />
        <div className={`absolute left-1/2 top-1/2 h-1 w-28 origin-left -translate-y-1/2 rounded-full bg-gradient-to-r ${config.accent}`} />
        {topNodes.map(({ label }, index) => (
          <span
            key={label}
            className="absolute h-3 w-3 rounded-full bg-sky-200"
            style={[
              { left: '31%', top: '25%' },
              { right: '28%', top: '32%' },
              { left: '40%', bottom: '22%' },
              { right: '36%', bottom: '28%' },
            ][index]}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="relative h-44 overflow-hidden bg-slate-950 p-5">
      <div className="absolute inset-0 visual-grid opacity-40" />
      <div className="relative flex h-full items-center justify-between">
        {topNodes.map(({ label, Icon }, index) => (
          <div key={label} className="relative z-10 flex h-20 w-20 items-center justify-center rounded-2xl border border-sky-300/30 bg-slate-900">
            <Icon className="h-6 w-6 text-sky-200" />
            {index < topNodes.length - 1 && <span className={`absolute left-full top-1/2 h-1 w-10 -translate-y-1/2 bg-gradient-to-r ${config.accent}`} />}
          </div>
        ))}
      </div>
    </div>
  )
}

export function BeforeAfterScenario({ variant }: { variant: VisualVariant }) {
  const config = visualConfigs[variant]

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ScenarioColumn tone="before" title="Before" items={config.before} />
      <ScenarioColumn tone="after" title="After" items={config.after} />
    </div>
  )
}

function ScenarioColumn({ items, title, tone }: { items: string[]; title: string; tone: 'after' | 'before' }) {
  const isAfter = tone === 'after'

  return (
    <div
      className={`rounded-[1.5rem] border p-5 ${
        isAfter
          ? 'border-sky-300/35 bg-sky-300/10'
          : 'border-slate-800 bg-slate-950'
      }`}
    >
      <p className={`font-mono text-sm font-black uppercase tracking-[0.18em] ${isAfter ? 'text-sky-200' : 'text-slate-500'}`}>
        {title}
      </p>
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <div key={item} className="flex gap-3 text-sm leading-6 text-slate-300">
            <span
              className={`mt-2 h-2 w-2 flex-none rounded-full ${
                isAfter ? 'bg-sky-300 shadow-[0_0_16px_rgba(125,211,252,0.7)]' : 'bg-slate-600'
              }`}
            />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function LeverageGraph({ variant }: { variant: VisualVariant }) {
  const config = visualConfigs[variant]

  return (
    <div className="rounded-[1.5rem] border border-slate-800 bg-slate-950 p-5 shadow-[0_24px_80px_rgba(2,6,23,0.3)]">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-mono text-sm font-black uppercase tracking-[0.18em] text-sky-300">Leverage graph</p>
          <h3 className="mt-2 text-2xl font-black text-white">What improves when the circuit is clear</h3>
        </div>
        <LineChart className="h-7 w-7 flex-none text-sky-300" />
      </div>
      <div className="mt-6 space-y-4">
        {config.graph.map((item) => (
          <div key={item.label}>
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-slate-300">{item.label}</span>
              <span className="font-mono text-sky-200">{item.value}%</span>
            </div>
            <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-800">
              <div className="h-full rounded-full bg-gradient-to-r from-sky-300 to-emerald-300" style={{ width: `${item.value}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
